import { useEffect, useState } from 'react'

import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { type IBudgetCategoryListViewFilters } from '@webapp/hooks/useBudgetCategoryListFilterManager'
import {
  CUSTOM_FIELD_FILTER_ROW_COUNT,
  getCustomFieldFilterKey,
  isCustomFieldFilter,
  toCustomFieldFilter,
} from '@webapp/hooks/useBudgetCategoryListFilterManager/utils'
import { type IBudgetCustomFieldConditionValue } from '@webapp/platform/budgets'

const EMPTY_CUSTOM_FIELD_FILTER_ROW: IBudgetCustomFieldConditionValue = {
  field: null,
  values: [],
}

const EMPTY_CUSTOM_FIELD_FILTER_ROWS = Array.from(
  { length: CUSTOM_FIELD_FILTER_ROW_COUNT },
  () => EMPTY_CUSTOM_FIELD_FILTER_ROW
)

const isFilledRow = (row: IBudgetCustomFieldConditionValue) =>
  Boolean(row.field) && row.values.length > 0

/**
 * Keeps the rows in their places when the filters change outside the rows,
 * for example from the URL, a saved filter or Clear filters. A row with a
 * field and no values is not a filter, so only the rows keep it.
 */
const syncRowsWithFilters = (
  rows: IBudgetCustomFieldConditionValue[],
  activeCustomFieldConditions: IBudgetCustomFieldConditionValue[]
) => {
  const activeConditionsByFieldId = new Map(
    activeCustomFieldConditions.map((condition) => [
      condition.field.id,
      condition,
    ])
  )

  const syncedRows = rows.map((row) => {
    const activeCondition = row.field
      ? activeConditionsByFieldId.get(row.field.id)
      : undefined

    if (activeCondition) return activeCondition

    return isFilledRow(row) ? EMPTY_CUSTOM_FIELD_FILTER_ROW : row
  })

  activeCustomFieldConditions.forEach((condition) => {
    const isShown = syncedRows.some(
      (row) => row.field?.id === condition.field.id
    )
    const emptyRowIndex = syncedRows.findIndex((row) => !row.field)

    if (!isShown && emptyRowIndex >= 0) {
      syncedRows[emptyRowIndex] = condition
    }
  })

  return syncedRows
}

export interface IUseBudgetCategoryListCustomFieldFilterRowsProps {
  filterManager: FilterManagerContextValues<IBudgetCategoryListViewFilters>
  onFilterChange: () => void
}

export const useBudgetCategoryListCustomFieldFilterRows = ({
  filterManager,
  onFilterChange,
}: IUseBudgetCategoryListCustomFieldFilterRowsProps) => {
  const { filters, addFilter, removeFilter } = filterManager

  const [rows, setRows] = useState(EMPTY_CUSTOM_FIELD_FILTER_ROWS)

  useEffect(() => {
    const activeCustomFieldConditions = Object.values(filters)
      .filter(isCustomFieldFilter)
      .map((customFieldFilter) => customFieldFilter.value)
      .filter((customFieldCondition) => Boolean(customFieldCondition?.field))

    setRows((currentRows) =>
      syncRowsWithFilters(currentRows, activeCustomFieldConditions)
    )
  }, [filters])

  const removeCustomFieldFilter = (fieldId: number) => {
    const filter = filters[getCustomFieldFilterKey(fieldId)]

    if (!filter) return false

    removeFilter(filter)
    return true
  }

  const changeRow = (
    rowIndex: number,
    nextRow: IBudgetCustomFieldConditionValue
  ) => {
    const previousRow = rows[rowIndex]

    setRows((currentRows) =>
      currentRows.map((row, index) => (index === rowIndex ? nextRow : row))
    )

    const hasRemovedPreviousFilter =
      previousRow.field && previousRow.field.id !== nextRow.field?.id
        ? removeCustomFieldFilter(previousRow.field.id)
        : false

    if (isFilledRow(nextRow)) {
      addFilter(toCustomFieldFilter(nextRow))
      onFilterChange()
      return
    }

    const hasRemovedNextFilter = nextRow.field
      ? removeCustomFieldFilter(nextRow.field.id)
      : false

    if (hasRemovedPreviousFilter || hasRemovedNextFilter) onFilterChange()
  }

  const clearRows = () => setRows(EMPTY_CUSTOM_FIELD_FILTER_ROWS)

  return { rows, changeRow, clearRows }
}
